/**
 * Анкета по шагам. Два режима:
 *
 *   mode="register" (по умолчанию) — первый экран сайта для гостя. Это МИНИ-анкета:
 *       пол, кого ищу, цель, имя, возраст, город — и сразу регистрация одним запросом
 *       POST /api/auth/register-with-profile/. Фото и остальное — уже после входа.
 *
 *   mode="profile" — пользователь вошёл, но анкета не дозаполнена (сразу после
 *       мини-анкеты, из Telegram/MAX или анкета заведена ботом). Шага регистрации нет,
 *       визард открывается на первом незаполненном шаге и досохраняет анкету:
 *       PATCH /api/profile/update/<id>/, а если анкеты нет — POST /api/profile/create-basic/.
 */
import AboutLink from '../Auth/AboutLink';
import SocialAuthButtons from '../Auth/SocialAuthButtons';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Cookies from 'js-cookie';

import { LayoutContext } from '../../context';
import { APICreateBaseProfile, APIGetProfile, APIUpdateBaseProfile } from '../../api';
import { APIPublicHobbies, APIPublicFields, APIRegisterWithProfile } from '../../api_auth';
import { getStoredRef, clearStoredRef } from '../../refStore';
import useAuthSuccess from '../Auth/useAuthSuccess';
import {
    AuthBackground, BrandSplash, Hero, IconButton, IconBack, ProgressBar, PrimaryButton,
    TextLink, SuccessBurst,
} from '../Auth/ui';
import { STEP_VIEWS } from './OnboardingSteps';
import {
    INITIAL_ANSWERS, DRAFT_KEYS, stepsForMode, firstUnansweredStep, validateStep, validateAccount,
    loadDraft, saveDraft, clearDraft, restoreStep,
    compressImage, photoPickError, photoTooBig,
    buildRegistrationFormData, buildProfileFormData, answersFromProfile, mapRegisterError,
} from './onboardingConfig';

const AUTO_ADVANCE_MS = 320;      // пауза, чтобы увидеть выбранную карточку
const SUCCESS_MS = 1600;          // анимация «Анкета создана»

export default function OnboardingPage({ mode = 'register' }) {
    const navigate = useNavigate();
    const applyAuth = useAuthSuccess();
    const reduceMotion = useReducedMotion();
    const { token, currentUser, profileStatus, checkProfileStatus } = useContext(LayoutContext);

    const isProfileMode = mode === 'profile';
    const draftKey = isProfileMode ? DRAFT_KEYS.profile : DRAFT_KEYS.register;

    const draft = useMemo(() => loadDraft(draftKey), [draftKey]);
    const [a, setA] = useState(() => ({
        ...INITIAL_ANSWERS,
        // у Telegram-пользователя уже есть имя — подставим, чтобы не вводил заново
        ...(isProfileMode && currentUser?.full_name ? { name: currentUser.full_name } : {}),
        ...(draft?.answers || {}),
    }));
    const [stepId, setStepId] = useState(() => restoreStep(draft, mode));
    const [dir, setDir] = useState(1);
    const [errors, setErrors] = useState({});
    const [lists, setLists] = useState({ hobbies: [], fields: [] });
    const [photo, setPhoto] = useState(null);          // { file, url } — только в памяти
    const [photoBusy, setPhotoBusy] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [existingProfileId, setExistingProfileId] = useState(null);
    // Анкета уже есть — подтянем её: нужен её id (дополняем, а не создаём заново)
    // и незаполненный шаг, с которого открыться.
    const needsPrefill = isProfileMode && Boolean(profileStatus?.has_profile);
    const [prefilling, setPrefilling] = useState(needsPrefill && !draft);

    const stepRef = useRef(stepId);
    stepRef.current = stepId;
    const photoRef = useRef(null);
    photoRef.current = photo;
    const timerRef = useRef(null);

    const steps = useMemo(() => stepsForMode(mode, a), [mode, a]);
    const index = Math.max(0, steps.findIndex((s) => s.id === stepId));
    const step = steps[index] || steps[0];
    const view = STEP_VIEWS[step.id];
    const isLast = index === steps.length - 1;

    // Справочники доступны без авторизации; если не загрузились — можно вписать своё
    useEffect(() => {
        let alive = true;
        Promise.allSettled([APIPublicHobbies(), APIPublicFields()]).then(([h, f]) => {
            if (!alive) return;
            setLists({
                hobbies: h.status === 'fulfilled' ? (h.value?.hobbies || []) : [],
                fields: f.status === 'fulfilled' ? (f.value?.fields || []) : [],
            });
        });
        return () => { alive = false; };
    }, []);

    // Анкета уже есть, но не дозаполнена — подтягиваем её и открываемся на первом
    // незаполненном шаге: после мини-анкеты это «рост и вес», у анкеты из бота — свой шаг.
    useEffect(() => {
        if (!needsPrefill) return undefined;
        if (!token) { setPrefilling(false); return undefined; }
        let alive = true;
        APIGetProfile(token)
            .then((profile) => {
                if (!alive || !profile?.id) return;
                setExistingProfileId(profile.id);
                const saved = profile.photo_file_path ? { url: profile.photo_file_path, file: null } : null;
                if (saved) setPhoto((prev) => prev || saved);
                // Черновик свежее анкеты с сервера — ответы и шаг тогда не трогаем
                if (draft) return;
                const filled = { ...INITIAL_ANSWERS, ...answersFromProfile(profile) };
                setA(filled);
                setStepId(firstUnansweredStep(filled, stepsForMode('profile', filled), { photo: saved }));
            })
            .catch(() => { /* не критично: пройдёт визард с начала */ })
            .finally(() => { if (alive) setPrefilling(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsPrefill, token]);

    // Черновик переживает перезагрузку страницы
    useEffect(() => { if (!done) saveDraft(a, step.id, draftKey); }, [a, step.id, done, draftKey]);

    useEffect(() => () => {
        clearTimeout(timerRef.current);
        if (photoRef.current?.url) URL.revokeObjectURL(photoRef.current.url);
    }, []);

    // ---------- навигация ----------
    const goTo = (id, direction) => {
        setErrors({});
        setDir(direction);
        setStepId(id);
        if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
    };

    const goNext = (answers, fromId) => {
        const list = stepsForMode(mode, answers);
        const i = list.findIndex((s) => s.id === fromId);
        const target = list[i + 1];
        if (target) goTo(target.id, 1);
    };

    const back = () => { if (index > 0) goTo(steps[index - 1].id, -1); };

    const patch = (p) => {
        setA((prev) => ({ ...prev, ...p }));
        setErrors((e) => (Object.keys(e).length ? {} : e));
    };

    /** Выбор карточкой: отметить и через мгновение уйти дальше. */
    const choose = (p) => {
        const from = stepRef.current;
        const nextAnswers = { ...a, ...p };
        setA(nextAnswers);
        setErrors({});
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (stepRef.current === from) goNext(nextAnswers, from);
        }, AUTO_ADVANCE_MS);
    };

    const tryNext = () => {
        const error = validateStep(step.id, a, { photo });
        if (error) { setErrors({ [step.id]: error }); return; }
        goNext(a, step.id);
    };

    // ---------- фото ----------
    const onPickPhoto = async (file) => {
        const pickError = photoPickError(file);
        if (pickError) { setErrors({ photo: pickError }); return; }
        setErrors({});
        setPhotoBusy(true);
        const ready = await compressImage(file);
        setPhotoBusy(false);
        if (photoTooBig(ready)) { setErrors({ photo: 'Фото слишком тяжёлое — выберите другое' }); return; }
        setPhoto((prev) => {
            if (prev?.url) URL.revokeObjectURL(prev.url);
            return { file: ready, url: URL.createObjectURL(ready) };
        });
    };

    // ---------- отправка ----------
    /** Гость: аккаунт + мини-анкета одним запросом. Фото и остальное — после входа. */
    const submitRegistration = async () => {
        const accountErrors = validateAccount(a);
        if (Object.keys(accountErrors).length) { setErrors(accountErrors); return; }

        setSubmitting(true);
        setErrors({});
        try {
            const response = await APIRegisterWithProfile(
                buildRegistrationFormData(a, photo?.file || null, getStoredRef()),
            );
            setDone(true);
            clearDraft(draftKey);
            clearStoredRef();
            setTimeout(() => {
                applyAuth(response, 'email');
                navigate('/', { replace: true });
            }, SUCCESS_MS);
        } catch (error) {
            setSubmitting(false);
            showSubmitError(error);
        }
    };

    /** Уже вошёл, анкеты нет: создаём анкету текущему пользователю. */
    const submitProfile = async () => {
        const photoError = validateStep('photo', a, { photo });
        if (photoError) { setErrors({ photo: photoError }); return; }

        setSubmitting(true);
        setErrors({});
        try {
            const formData = buildProfileFormData(a, photo?.file || null);
            const ref = getStoredRef();
            if (ref) formData.append('ref', ref);
            if (existingProfileId) await APIUpdateBaseProfile(token, existingProfileId, formData);
            else await APICreateBaseProfile(token, formData);
            setDone(true);
            clearDraft(draftKey);
            clearStoredRef();
            setTimeout(() => {
                // контекст сам закроет анкету и покажет ленту
                if (currentUser?.id) checkProfileStatus(token, currentUser.id);
            }, SUCCESS_MS);
        } catch (error) {
            setSubmitting(false);
            showSubmitError(error);
        }
    };

    const showSubmitError = (error) => {
        const mapped = mapRegisterError(error, steps);
        if (mapped.stepId && mapped.stepId !== step.id) {
            goTo(mapped.stepId, -1);
            setErrors({ [mapped.stepId]: mapped.formError });
        } else {
            setErrors({ ...(mapped.fieldErrors || {}), ...(mapped.formError ? { form: mapped.formError } : {}) });
        }
    };

    const logout = () => {
        Cookies.remove('authToken');
        Cookies.remove('authPlatform');
        try { localStorage.removeItem('authPlatform'); } catch (_e) { /* ignore */ }
        window.location.href = '/login';
    };

    // ---------- отрисовка ----------
    const shift = reduceMotion ? 0 : 48;
    const variants = {
        enter: (d) => ({ opacity: 0, x: d * shift }),
        center: { opacity: 1, x: 0 },
        exit: (d) => ({ opacity: 0, x: d * -shift }),
    };

    const isAccountStep = step.id === 'account';
    const submitNow = isProfileMode ? submitProfile : submitRegistration;
    const showCta = !view.auto || (isProfileMode && isLast);
    const ctaLabel = (isProfileMode && isLast)
        ? 'Сохранить анкету'
        : (view.cta ? view.cta(a) : 'Далее');
    const onCta = (isAccountStep || (isProfileMode && isLast)) ? submitNow : tryNext;

    // Ждём анкету с сервера, чтобы не мигнуть первым шагом и сразу открыть нужный
    if (prefilling) return <BrandSplash />;

    return (
        <AuthBackground>
            <div className="lv-screen">
                <header className="lv-onb__top">
                    <IconButton label="Назад" onClick={back} hidden={index === 0}><IconBack /></IconButton>
                    <ProgressBar value={(index + 1) / steps.length} />
                    <span className="lv-onb__count">{index + 1}/{steps.length}</span>
                </header>

                <AnimatePresence mode="wait" initial={false} custom={dir}>
                    <motion.main key={step.id} className="lv-onb__body" custom={dir} variants={variants}
                                 initial="enter" animate="center" exit="exit"
                                 transition={{ type: 'spring', stiffness: 360, damping: 34, opacity: { duration: 0.18 } }}>
                        {view.hero && !isProfileMode ? <Hero /> : null}
                        <motion.h1 className="lv-title"
                                   initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                   transition={{ delay: 0.04 }}>
                            {view.title}
                        </motion.h1>
                        {view.subtitle ? (
                            <motion.p className="lv-subtitle"
                                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                      transition={{ delay: 0.08 }}>
                                {view.subtitle}
                            </motion.p>
                        ) : null}
                        <div className="lv-content">
                            <view.Content
                                a={a} patch={patch} choose={choose} next={tryNext}
                                lists={lists} errors={errors} error={errors[step.id]}
                                photo={photo} photoBusy={photoBusy} onPickPhoto={onPickPhoto}
                            />
                        </div>
                    </motion.main>
                </AnimatePresence>

                <footer className="lv-onb__footer">
                    {!isProfileMode && index === 0 ? <SocialAuthButtons /> : null}
                    {showCta ? (
                        <PrimaryButton onClick={onCta} loading={submitting}>
                            {submitting ? 'Сохраняем анкету…' : ctaLabel}
                        </PrimaryButton>
                    ) : null}
                    {isProfileMode ? (
                        <TextLink onClick={logout}>Выйти из аккаунта</TextLink>
                    ) : (
                        <TextLink onClick={() => navigate('/login')}>
                            {isAccountStep ? 'Уже есть анкета? Войти' : 'У меня есть анкета'}
                        </TextLink>
                    )}
                    <AboutLink />
                </footer>
            </div>

            <AnimatePresence>
                {done ? (
                    <SuccessBurst
                        title={isProfileMode ? 'Анкета готова!' : 'Анкета создана!'}
                        subtitle="Добро пожаловать в Lovirds"
                    />
                ) : null}
            </AnimatePresence>
        </AuthBackground>
    );
}
